// allergens — the canonical allergen taxonomy + matcher for the user's
// stored allergy list (profiles.allergies, migration 0276).
//
// Allergies are SAFETY-CRITICAL, not a soft preference. Storage holds the
// user's chosen allergen ids (from ALLERGENS below) plus any free-text
// customs. Everything that needs to ask "does this food conflict with the
// user's allergies?" — the generator guard, pantry/recipe warnings —
// routes through findAllergyConflicts() so the match logic is ONE thing.
//
// The Big-9 US major allergens (sesame added 2023). `terms` are the
// lowercase substrings we match against ingredient text + parsed
// "Contains:" allergen lines. Erring toward OVER-matching is correct here:
// a false "contains tree nut" warning is annoying; a missed one is unsafe.

export const ALLERGENS = [
  { id: "milk",      label: "Milk / Dairy", emoji: "🥛",
    terms: ["milk", "dairy", "cream", "butter", "cheese", "yogurt", "yoghurt",
            "casein", "caseinate", "whey", "lactose", "ghee", "custard", "curd"] },
  { id: "egg",       label: "Egg", emoji: "🥚",
    terms: ["egg", "eggs", "albumin", "albumen", "mayonnaise", "meringue", "aioli"] },
  { id: "peanut",    label: "Peanut", emoji: "🥜",
    terms: ["peanut", "peanuts", "groundnut", "arachis"] },
  { id: "tree_nut",  label: "Tree nut", emoji: "🌰",
    terms: ["almond", "walnut", "pecan", "cashew", "pistachio", "hazelnut",
            "macadamia", "brazil nut", "pine nut", "chestnut", "praline",
            "marzipan", "nutella", "nut "] },
  { id: "soy",       label: "Soy", emoji: "🫘",
    terms: ["soy", "soya", "soybean", "edamame", "tofu", "tempeh", "miso",
            "natto", "tamari", "textured vegetable protein", "tvp"] },
  { id: "wheat",     label: "Wheat / Gluten", emoji: "🌾",
    terms: ["wheat", "gluten", "flour", "semolina", "durum", "spelt", "farro",
            "barley", "rye", "malt", "couscous", "bulgur", "seitan", "bread crumb",
            "breadcrumb", "panko"] },
  { id: "fish",      label: "Fish", emoji: "🐟",
    terms: ["fish", "anchovy", "anchovies", "cod", "salmon", "tuna", "tilapia",
            "halibut", "trout", "sardine", "mackerel", "bass", "snapper",
            "worcestershire", "fish sauce", "nuoc mam"] },
  { id: "shellfish", label: "Shellfish", emoji: "🦐",
    terms: ["shellfish", "shrimp", "prawn", "crab", "lobster", "crawfish",
            "crayfish", "langoustine", "scallop", "clam", "mussel", "oyster",
            "squid", "calamari", "octopus", "crustacean", "mollusk", "mollusc"] },
  { id: "sesame",    label: "Sesame", emoji: "◦",
    terms: ["sesame", "tahini", "benne", "halvah", "halva", "gomashio"] },
];

const ALLERGEN_BY_ID = new Map(ALLERGENS.map((a) => [a.id, a]));

export function allergenLabel(id) {
  return ALLERGEN_BY_ID.get(id)?.label || id;
}

// Common allergen-free substitutes, keyed by the allergen LABEL (what the
// matcher returns). Used to make the "make it allergen-free" suggestion
// concrete in the conflict gate ("we'll swap dairy for oat or cashew
// cream"). These are illustrative — the generator picks the contextually
// right swap per dish and names it; this just seeds the suggestion.
const ALLERGEN_SUBSTITUTES = {
  "Milk / Dairy":   ["oat milk", "cashew cream", "coconut milk"],
  "Egg":            ["flax egg", "aquafaba", "applesauce"],
  "Peanut":         ["sunflower seed butter", "roasted chickpeas"],
  "Tree nut":       ["sunflower seeds", "pumpkin seeds", "roasted chickpeas"],
  "Soy":            ["coconut aminos", "chickpea miso"],
  "Wheat / Gluten": ["gluten-free flour blend", "rice flour", "almond flour"],
  "Fish":           ["chicken", "hearts of palm", "jackfruit"],
  "Shellfish":      ["chicken", "hearts of palm", "king oyster mushroom"],
  "Sesame":         ["sunflower seed butter", "poppy seed"],
};

// Build a short "e.g. X, Y" suggestion string for a set of matched
// allergen labels. Returns "" when none of them have a known sub (custom
// allergens) — the generator still substitutes intelligently, we just
// don't preview a specific swap.
export function suggestSubstitutes(allergenLabels) {
  if (!Array.isArray(allergenLabels)) return "";
  const subs = [];
  for (const label of allergenLabels) {
    const opts = ALLERGEN_SUBSTITUTES[label];
    if (opts && opts.length) subs.push(`${label.toLowerCase()} → ${opts.slice(0, 2).join(" / ")}`);
  }
  return subs.join("; ");
}

// Human-readable exclusion lines for the recipe-generation + clarify
// prompts: "Milk / Dairy (milk, cream, butter, cheese, casein, whey…)".
// The edge fn renders these verbatim as an ABSOLUTE exclusion block, so
// the allergen taxonomy stays in ONE place (here) and the server stays
// dumb. Custom free-text allergens render as-is.
export function allergenExclusionStrings(stored) {
  return normalizeUserAllergies(stored).map((spec) => {
    const known = ALLERGEN_BY_ID.get(spec.id);
    if (!known) return spec.label;                 // custom free-text
    const examples = known.terms
      .filter((t) => t.trim() && t.trim() !== known.id)
      .slice(0, 8)
      .map((t) => t.trim());
    return examples.length ? `${known.label} (${examples.join(", ")})` : known.label;
  });
}

// Normalize a stored allergies value (array of ids and/or free-text) into
// a clean list of { id, label, terms } match specs. Custom free-text
// becomes its own spec whose term IS the text (lowercased).
export function normalizeUserAllergies(stored) {
  if (!Array.isArray(stored)) return [];
  const out = [];
  const seen = new Set();
  for (const raw of stored) {
    const val = typeof raw === "string" ? raw.trim() : "";
    if (!val || seen.has(val.toLowerCase())) continue;
    seen.add(val.toLowerCase());
    const known = ALLERGEN_BY_ID.get(val);
    if (known) {
      out.push({ id: known.id, label: known.label, terms: known.terms });
    } else {
      // Free-text custom allergen ("kiwi", "mustard"). Match on the word.
      const t = val.toLowerCase();
      out.push({ id: `custom:${t}`, label: val, terms: [t] });
    }
  }
  return out;
}

// Common foods that BEGIN with an allergen term but don't actually
// contain the allergen. The matcher below is intentionally a left-
// bounded PREFIX match (so compound words like "milkshake" /
// "cheeseburger" / "eggnog" are caught), which means the safe-default
// of over-matching would otherwise fire on these. Curated denylist,
// added sparingly — a missed allergen is unsafe; a false positive is
// only annoying, so the bar to add here is "common food, definitely
// allergen-free." This is the curated tier of the same
// curated/derived/bridge cascade used elsewhere; keep it small.
const ALLERGEN_FALSE_POSITIVES = new Set([
  "eggplant", "eggplants",      // egg
  "crabapple", "crabapples",    // shellfish (crab)
]);

// Does `haystack` (ingredient text, recipe ingredient names joined, a
// product name, etc.) conflict with any of the user's allergies? Returns
// the list of matched allergen labels (empty = clear).
//
// Matching is LEFT-BOUNDED PREFIX: a term hits when it starts a word
// (string start or a non-letter before it) and may be glued to more
// letters on the right. So "milk" matches "whole milk", "milk, soy",
// AND "milkshake"/"milky" — the compound-word case that a both-sides-
// bounded match used to miss (the literal bug behind "milkshake didn't
// warn"). Erring toward over-matching is correct for safety; the small
// ALLERGEN_FALSE_POSITIVES denylist screens out the handful of common
// foods (eggplant, crabapple) that would otherwise false-fire. "nut "
// (trailing space) stays a pre-bounded exact-substring term so it keeps
// dodging "doughnut"/"butternut" while catching "nut blend".
export function findAllergyConflicts(haystack, userAllergies) {
  const specs = Array.isArray(userAllergies) && userAllergies.length
    ? (typeof userAllergies[0] === "object" ? userAllergies : normalizeUserAllergies(userAllergies))
    : [];
  if (!specs.length) return [];
  const text = String(haystack || "").toLowerCase();
  if (!text) return [];
  const hits = [];
  for (const spec of specs) {
    const found = spec.terms.some((term) => termHits(text, term));
    if (found) hits.push(spec.label);
  }
  return hits;
}

// True if `term` occurs in `text` as the start of a word. Trailing
// letters are allowed (prefix match) so compounds are caught; the full
// matched word is screened against ALLERGEN_FALSE_POSITIVES so curated
// non-allergen foods don't trip the safe-default over-match.
function termHits(text, term) {
  if (term.endsWith(" ")) return text.includes(term);   // pre-bounded (e.g. "nut ")
  const re = new RegExp(`(?:^|[^a-z])(${escapeRe(term)}[a-z]*)`, "gi");
  let m;
  while ((m = re.exec(text)) !== null) {
    const word = m[1].toLowerCase();             // term + any trailing letters
    if (!ALLERGEN_FALSE_POSITIVES.has(word)) return true;
    // Denylisted word (e.g. "eggplant") — keep scanning; a later
    // occurrence in the same text could still be a real hit.
  }
  return false;
}

// Match against a structured allergen list (the parsed "Contains:" line
// from nutrition.js — already allergen tokens like ["milk","soy"]). Looser
// than the text matcher: any term-overlap counts.
export function findAllergyConflictsInList(allergenTokens, userAllergies) {
  if (!Array.isArray(allergenTokens) || !allergenTokens.length) return [];
  return findAllergyConflicts(allergenTokens.join(" , "), userAllergies);
}

function escapeRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Recipe-level convenience: joins a recipe's ingredient names (both
// the bundled `item` field and user-recipe `name` variants, plus
// notes) into one haystack and delegates to findAllergyConflicts.
// Returns the matched allergen LABELS (["Peanut"]) — empty when safe
// or when the user has no allergies. Used by recommendation surfaces:
// auto-recommenders HARD-EXCLUDE on non-empty; browsable lists render
// warn chips ("CONTAINS PEANUT") instead of hiding.
export function recipeAllergyConflicts(recipe, userAllergies) {
  if (!recipe || !Array.isArray(userAllergies) || userAllergies.length === 0) return [];
  const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
  const haystack = [
    recipe.title || "",
    ...ingredients.map((i) => [i?.item, i?.name, i?.note].filter(Boolean).join(" ")),
  ].join(" , ");
  return findAllergyConflicts(haystack, userAllergies);
}
